/**
 * HudManager — Root orchestrator for all UI overlay components.
 *
 * Creates sub-containers, instantiates every panel, and exposes
 * convenience methods for the game loop and network layer.
 */

import { Leaderboard } from './Leaderboard.js';
import { AlgorithmCardBar } from './AlgorithmCardBar.js';
import { DroneStatusPanel } from './DroneStatusPanel.js';
import { MinimapRenderer } from './MinimapRenderer.js';
import { UsernamePrompt, type GameMode } from './UsernamePrompt.js';
import { LoadoutScreen } from './LoadoutScreen.js';
import { LobbyScreen } from './LobbyScreen.js';
import { BrStatusPanel } from './BrStatusPanel.js';
import { buildPostMatchStatsTable, type PostMatchPlayerStats } from './PostMatchStats.js';
import { RematchModal } from './RematchModal.js';
import { ConnectionStatus, type ConnectionState } from './ConnectionStatus.js';
import { CombatLog, type CombatLogDeps } from './CombatLog.js';
import { KillStreakToast } from './KillStreakToast.js';
import { Crosshair } from './Crosshair.js';

export class HudManager {
  private root: HTMLElement;
  private leaderboard: Leaderboard;
  private cardBar: AlgorithmCardBar;
  private droneStatus: DroneStatusPanel;
  private minimap: MinimapRenderer;
  private usernamePrompt: UsernamePrompt;
  private loadoutScreen: LoadoutScreen;
  private lobbyScreen: LobbyScreen;
  private brStatus: BrStatusPanel;
  private hud: HTMLElement;
  private killFeedContainer: HTMLElement;
  private respawnOverlay: HTMLElement | null = null;
  private respawnTimer: ReturnType<typeof setInterval> | null = null;
  private controlsPanel: HTMLElement;
  private matchEndOverlay: HTMLElement | null = null;
  private eliminatedOverlay: HTMLElement | null = null;
  private eliminatedTimer: ReturnType<typeof setTimeout> | null = null;
  private hitMarkersLayer: HTMLElement;
  private damageFloatersLayer: HTMLElement;
  private damageDirectionLayer: HTMLElement;
  private spectateClickHandler: (() => void) | null = null;
  private killedByCaption: HTMLElement | null = null;
  private rematchModal!: RematchModal;
  private rematchButton: HTMLButtonElement | null = null;
  private rematchClickHandler: (() => void) | null = null;
  private menuClickHandler: (() => void) | null = null;
  private connectionStatus!: ConnectionStatus;
  private reconnectOverlay!: HTMLElement;
  private reconnectTitle!: HTMLElement;
  private reconnectSubtitle!: HTMLElement;
  private combatLog: CombatLog | null = null;
  private killStreakToast!: KillStreakToast;
  private crosshair!: Crosshair;

  constructor(rootElement: HTMLElement) {
    this.root = rootElement;

    this.hud = document.createElement('div');
    this.hud.className = 'game-hud hidden';
    this.root.appendChild(this.hud);

    this.killFeedContainer = document.createElement('div');
    this.killFeedContainer.className = 'kill-feed';
    this.hud.appendChild(this.killFeedContainer);

    this.leaderboard = new Leaderboard(this.hud);
    this.cardBar = new AlgorithmCardBar(this.hud);
    this.droneStatus = new DroneStatusPanel(this.hud);
    this.minimap = new MinimapRenderer(this.hud);
    this.brStatus = new BrStatusPanel(this.hud);
    this.usernamePrompt = new UsernamePrompt(this.root);
    this.loadoutScreen = new LoadoutScreen(this.root);
    this.lobbyScreen = new LobbyScreen(this.root);

    // Controls toggle + panel
    this.controlsPanel = this.buildControlsPanel();
    this.hud.appendChild(this.controlsPanel);

    // Three transparent layers stacked over the HUD root for combat-feedback
    // plugins. Each plugin owns its layer's contents; HudManager just provides
    // the slot. Pointer-events: none on the container so they never steal clicks.
    this.damageDirectionLayer = document.createElement('div');
    this.damageDirectionLayer.id = 'damage-direction-layer';
    this.damageDirectionLayer.className = 'combat-feedback-layer';
    this.hud.appendChild(this.damageDirectionLayer);

    this.damageFloatersLayer = document.createElement('div');
    this.damageFloatersLayer.id = 'damage-floaters-layer';
    this.damageFloatersLayer.className = 'combat-feedback-layer';
    this.hud.appendChild(this.damageFloatersLayer);

    this.hitMarkersLayer = document.createElement('div');
    this.hitMarkersLayer.id = 'hit-markers-layer';
    this.hitMarkersLayer.className = 'combat-feedback-layer';
    this.hud.appendChild(this.hitMarkersLayer);
    // Modal is parented to the root so it floats over the match-end overlay
    // (which is also rooted there). Keeps z-stacking simple.
    this.rematchModal = new RematchModal(this.root);

    // Sits ABOVE the leaderboard in the top-right; visible whether or not
    // the rest of the game HUD is shown so disconnects mid-loading are clear.
    this.connectionStatus = new ConnectionStatus(this.hud);
    this.reconnectOverlay = this.buildReconnectOverlay();
    this.root.appendChild(this.reconnectOverlay);

    // Bottom-center kill-streak announcer. Lives on the HUD root so it
    // overlays the play area and is hidden when the HUD is hidden.
    this.killStreakToast = new KillStreakToast(this.hud);

    // FPS-mode center crosshair. Parented to the HUD root (not `this.hud`)
    // so it stays visible whether or not the game HUD is shown — visibility
    // is driven exclusively by pointer-lock state from GameClient.
    this.crosshair = new Crosshair(this.root);
  }

  getCrosshair(): Crosshair {
    return this.crosshair;
  }

  getHitMarkersLayer(): HTMLElement {
    return this.hitMarkersLayer;
  }

  getDamageFloatersLayer(): HTMLElement {
    return this.damageFloatersLayer;
  }

  getDamageDirectionLayer(): HTMLElement {
    return this.damageDirectionLayer;
  }

  /**
   * Root container the HUD was mounted into (`#hud-root`). Exposed so siblings
   * like the MouseSettingsPanel can attach floating overlays without coupling
   * to the DOM id directly.
   */
  getRoot(): HTMLElement {
    return this.root;
  }

  private buildReconnectOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'reconnect-overlay hidden';
    overlay.id = 'reconnect-overlay';

    const spinner = document.createElement('div');
    spinner.className = 'reconnect-spinner';
    overlay.appendChild(spinner);

    this.reconnectTitle = document.createElement('div');
    this.reconnectTitle.className = 'reconnect-title';
    this.reconnectTitle.textContent = 'RECONNECTING';
    overlay.appendChild(this.reconnectTitle);

    this.reconnectSubtitle = document.createElement('div');
    this.reconnectSubtitle.className = 'reconnect-subtitle';
    this.reconnectSubtitle.textContent = 'Re-establishing connection to server';
    overlay.appendChild(this.reconnectSubtitle);

    return overlay;
  }

  setConnectionState(state: ConnectionState): void {
    this.connectionStatus.setConnectionState(state);
    const showOverlay = state === 'reconnecting' || state === 'closed';
    this.reconnectOverlay.classList.toggle('hidden', !showOverlay);
    if (!showOverlay) return;
    this.reconnectTitle.textContent = state === 'closed' ? 'OFFLINE' : 'RECONNECTING';
    this.reconnectSubtitle.textContent = state === 'closed'
      ? 'Connection lost. Refresh the page to retry.'
      : 'Re-establishing connection to server';
  }

  setPing(ms: number): void {
    this.connectionStatus.setPing(ms);
  }

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  private buildControlsPanel(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'controls-wrapper';

    const toggle = document.createElement('button');
    toggle.className = 'controls-toggle';
    toggle.textContent = '?';
    toggle.title = 'Controls';
    wrapper.appendChild(toggle);

    const panel = document.createElement('div');
    panel.className = 'controls-panel hidden';
    panel.innerHTML = `
      <div class="controls-title">CONTROLS</div>
      <div class="controls-section">
        <div class="controls-heading">Movement</div>
        <div class="controls-row"><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> <span>Move</span></div>
        <div class="controls-row"><kbd>Space</kbd> <span>Ascend</span></div>
        <div class="controls-row"><kbd>Shift</kbd> <span>Descend</span></div>
      </div>
      <div class="controls-section">
        <div class="controls-heading">Combat</div>
        <div class="controls-row"><kbd>1</kbd>-<kbd>6</kbd> <span>Activate card</span></div>
        <div class="controls-row"><kbd>E</kbd> <span>Basic fire</span></div>
        <div class="controls-row"><kbd>Q</kbd> <span>Selected ability</span></div>
        <div class="controls-row"><kbd>U</kbd><kbd>I</kbd><kbd>O</kbd><kbd>J</kbd><kbd>K</kbd><kbd>L</kbd> <span>Drone skills</span></div>
        <div class="controls-row"><kbd>Left click</kbd> <span>Set target</span></div>
        <div class="controls-row"><kbd>Esc</kbd> <span>Cancel order</span></div>
      </div>
      <div class="controls-section">
        <div class="controls-heading">Camera</div>
        <div class="controls-row"><kbd>Right hold</kbd> <span>Look</span></div>
        <div class="controls-row"><kbd>Ctrl</kbd>/<kbd>Option</kbd> + <kbd>drag</kbd> <span>Trackpad look</span></div>
        <div class="controls-row"><kbd>Scroll</kbd> <span>Zoom</span></div>
      </div>
    `;
    wrapper.appendChild(panel);

    toggle.addEventListener('click', () => {
      panel.classList.toggle('hidden');
      toggle.textContent = panel.classList.contains('hidden') ? '?' : 'X';
    });

    return wrapper;
  }

  showUsernamePrompt(
    onSubmit: (username: string, mode: GameMode) => void,
    presetMode?: GameMode,
  ): void {
    this.usernamePrompt.show(onSubmit, presetMode);
  }

  showLoadoutScreen(
    onSubmit: (loadout: number[]) => void,
    presetLoadout?: number[],
  ): void {
    this.usernamePrompt.hide();
    this.loadoutScreen.show(onSubmit, presetLoadout);
  }

  showGameHud(): void {
    this.usernamePrompt.hide();
    this.loadoutScreen.hide();
    this.hud.classList.remove('hidden');
    this.leaderboard.show();
    this.cardBar.show();
    this.droneStatus.show();
    this.minimap.show();
  }

  hideGameHud(): void {
    this.leaderboard.hide();
    this.cardBar.hide();
    this.droneStatus.hide();
    this.minimap.hide();
  }

  showLobby(onLeave: () => void): void {
    this.hideGameHud();
    this.lobbyScreen.show(onLeave);
  }

  hideLobby(): void {
    this.lobbyScreen.hide();
  }

  getLobbyScreen(): LobbyScreen {
    return this.lobbyScreen;
  }

  showRespawnOverlay(countdownMs: number): void {
    this.hideRespawnOverlay();

    const overlay = document.createElement('div');
    overlay.className = 'respawn-overlay';

    const title = document.createElement('div');
    title.className = 'respawn-title';
    title.textContent = 'DESTROYED';
    overlay.appendChild(title);

    const countdown = document.createElement('div');
    countdown.className = 'respawn-countdown';
    overlay.appendChild(countdown);

    const subtitle = document.createElement('div');
    subtitle.className = 'respawn-subtitle';
    subtitle.textContent = 'Redeploying...';
    overlay.appendChild(subtitle);

    this.root.appendChild(overlay);
    this.respawnOverlay = overlay;

    let remaining = Math.ceil(countdownMs / 1000);
    countdown.textContent = String(remaining);

    this.respawnTimer = setInterval(() => {
      remaining--;
      if (remaining <= 0) {
        this.hideRespawnOverlay();
      } else {
        countdown.textContent = String(remaining);
      }
    }, 1000);
  }

  hideRespawnOverlay(): void {
    if (this.respawnTimer !== null) {
      clearInterval(this.respawnTimer);
      this.respawnTimer = null;
    }
    if (this.respawnOverlay) {
      this.respawnOverlay.classList.add('hiding');
      const el = this.respawnOverlay;
      const onEnd = () => {
        el.remove();
        el.removeEventListener('animationend', onEnd);
      };
      el.addEventListener('animationend', onEnd);
      // Fallback: if animationend never fires, remove after 1 second
      setTimeout(() => el.remove(), 1000);
      this.respawnOverlay = null;
    }
  }

  showPickupNotification(message: string, tierColor: string): void {
    const note = document.createElement('div');
    note.className = 'kill-notification';
    note.innerHTML = `<span style="color:${tierColor};font-weight:700">+ ${this.escapeHtml(message)}</span>`;

    this.killFeedContainer.appendChild(note);

    while (this.killFeedContainer.children.length > 5) {
      this.killFeedContainer.removeChild(this.killFeedContainer.children[0]);
    }

    setTimeout(() => {
      note.classList.add('fading');
      setTimeout(() => note.remove(), 400);
    }, 3000);
  }

  showKillNotification(killer: string, victim: string): void {
    const note = document.createElement('div');
    note.className = 'kill-notification';
    note.innerHTML =
      `<span class="killer-name">${this.escapeHtml(killer)}</span>` +
      `<span class="kill-separator">\u25C6</span>` +
      `<span class="victim-name">${this.escapeHtml(victim)}</span>`;

    this.appendToKillFeed(note, 4000);
  }

  /**
   * BR elimination feed entry. Red "ELIMINATED" label instead of the cyan
   * killer→victim format. Optionally shows the eliminator.
   */
  showEliminationNotification(payload: {
    username: string;
    placement: number;
    eliminatedBy?: string;
  }): void {
    const note = document.createElement('div');
    note.className = 'kill-notification elimination-notification';
    const byFragment = payload.eliminatedBy
      ? `<span class="kill-separator">\u25C6</span>` +
        `<span class="killer-name">${this.escapeHtml(payload.eliminatedBy)}</span>`
      : '';
    note.innerHTML =
      `<span class="elim-label">ELIMINATED</span>` +
      `<span class="victim-name">${this.escapeHtml(payload.username)}</span>` +
      `<span class="elim-rank">#${payload.placement}</span>` +
      byFragment;

    this.appendToKillFeed(note, 5000);
  }

  /**
   * Generic top-feed toast (used for zone-shrink warnings, match-start banners).
   * Reuses kill-feed infra so the notification lines up consistently.
   */
  showToast(message: string, accent: 'amber' | 'cyan' | 'danger' = 'cyan'): void {
    const note = document.createElement('div');
    note.className = `kill-notification br-toast br-toast-${accent}`;
    note.textContent = message;
    this.appendToKillFeed(note, 3500);
  }

  /**
   * Brief warning toast for `CardEffectFailed` events — surfaces the reason
   * (e.g. ChoreoUltimate cohesion fail) so the player knows the card slot
   * fired but the precondition wasn't met. Routed through the danger accent
   * so it's visually distinct from regular status banners.
   */
  showCardFailedToast(message: string): void {
    this.showToast(message, 'danger');
  }

  /**
   * Full-screen overlay shown to the local player when they are eliminated
   * in Battle Royale. Includes final rank and a Spectate/Leave button.
   * Auto-dismisses after `autoDismissMs` (default 8s).
   */
  showEliminatedOverlay(
    placement: number,
    total: number,
    autoDismissMs: number = 8000,
  ): void {
    this.hideEliminatedOverlay();

    const overlay = document.createElement('div');
    overlay.className = 'eliminated-overlay';

    const title = document.createElement('div');
    title.className = 'eliminated-title';
    title.textContent = 'ELIMINATED';
    overlay.appendChild(title);

    const rank = document.createElement('div');
    rank.className = 'eliminated-rank';
    rank.textContent = `RANK ${placement}/${total}`;
    overlay.appendChild(rank);

    const subtitle = document.createElement('div');
    subtitle.className = 'eliminated-subtitle';
    subtitle.textContent = 'You are out of the match';
    overlay.appendChild(subtitle);

    const actionRow = document.createElement('div');
    actionRow.className = 'eliminated-actions';

    const spectateBtn = document.createElement('button');
    spectateBtn.className = 'eliminated-btn';
    spectateBtn.textContent = 'SPECTATE';
    spectateBtn.addEventListener('click', () => {
      this.hideEliminatedOverlay();
      this.spectateClickHandler?.();
    });
    actionRow.appendChild(spectateBtn);

    overlay.appendChild(actionRow);

    this.root.appendChild(overlay);
    this.eliminatedOverlay = overlay;

    if (autoDismissMs > 0) {
      this.eliminatedTimer = setTimeout(
        () => this.hideEliminatedOverlay(),
        autoDismissMs,
      );
    }
  }

  hideEliminatedOverlay(): void {
    if (this.eliminatedTimer !== null) {
      clearTimeout(this.eliminatedTimer);
      this.eliminatedTimer = null;
    }
    if (this.eliminatedOverlay) {
      const el = this.eliminatedOverlay;
      el.classList.add('hiding');
      setTimeout(() => el.remove(), 400);
      this.eliminatedOverlay = null;
    }
  }

  /**
   * Register the click handler for the existing inert SPECTATE button shown
   * on the elimination overlay. Called by GameClient at boot. Without this
   * the button merely closes the overlay (its prior behavior).
   */
  onSpectateClick(handler: () => void): void {
    this.spectateClickHandler = handler;
  }

  /**
   * Show/hide the "KILLED BY: <name>" caption used during the kill-cam.
   * Lazily creates the element on first call. An empty `text` hides it.
   */
  setKilledByCaption(text: string): void {
    if (!text) {
      if (this.killedByCaption) this.killedByCaption.style.display = 'none';
      return;
    }
    if (!this.killedByCaption) {
      const el = document.createElement('div');
      el.className = 'killed-by-caption';
      el.style.cssText = [
        'position:fixed',
        'top:24%',
        'left:50%',
        'transform:translateX(-50%)',
        'font-family:inherit',
        'font-size:14px',
        'font-weight:700',
        'letter-spacing:6px',
        'text-transform:uppercase',
        'color:#ff5577',
        'text-shadow:0 0 12px rgba(255,85,119,0.6)',
        'background:rgba(0,0,0,0.55)',
        'padding:8px 22px',
        'border:1px solid rgba(255,85,119,0.6)',
        'border-radius:4px',
        'pointer-events:none',
        'z-index:1500',
        'display:none',
      ].join(';');
      this.root.appendChild(el);
      this.killedByCaption = el;
    }
    this.killedByCaption.textContent = text;
    this.killedByCaption.style.display = 'block';
  }

  private appendToKillFeed(note: HTMLElement, ttlMs: number): void {
    this.killFeedContainer.appendChild(note);

    // Cap at 5 visible notifications
    while (this.killFeedContainer.children.length > 5) {
      this.killFeedContainer.removeChild(this.killFeedContainer.children[0]);
    }

    setTimeout(() => {
      note.classList.add('fading');
      setTimeout(() => note.remove(), 400);
    }, ttlMs);
  }

  /**
   * Show the Match Over overlay with a final leaderboard. Idempotent —
   * calling with the same entries replaces the existing panel in place.
   * `entries` should be pre-sorted (highest score first).
   *
   * If `richStats` is provided (free-mode `MatchWon` payload), render the
   * extended stats table instead of the basic 4-column leaderboard. The
   * basic `entries` path stays as a backward-compatible fallback for callers
   * that don't have the rich stats yet (e.g. snapshot-only fallback, BR).
   */
  showMatchEndOverlay(
    entries: Array<{ username: string; score: number; kills: number }>,
    localUsername: string,
    richStats?: ReadonlyArray<PostMatchPlayerStats>,
  ): void {
    if (this.matchEndOverlay) return; // only show once per match

    const overlay = document.createElement('div');
    overlay.className = 'match-end-overlay';

    const title = document.createElement('div');
    title.className = 'match-end-title';
    title.textContent = 'MATCH OVER';
    overlay.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'match-end-subtitle';
    const winner = entries[0];
    subtitle.textContent = winner ? `Winner: ${winner.username}` : 'No survivors';
    overlay.appendChild(subtitle);

    if (richStats && richStats.length > 0) {
      overlay.appendChild(buildPostMatchStatsTable(richStats, localUsername));
    } else {
      const table = document.createElement('div');
      table.className = 'match-end-leaderboard';
      const header = document.createElement('div');
      header.className = 'match-end-row header';
      header.innerHTML = `<span>RANK</span><span>PLAYER</span><span>SCORE</span><span>KILLS</span>`;
      table.appendChild(header);

      entries.forEach((entry, i) => {
        const row = document.createElement('div');
        row.className = 'match-end-row' + (entry.username === localUsername ? ' self' : '');
        row.innerHTML =
          `<span class="rank">#${i + 1}</span>` +
          `<span>${this.escapeHtml(entry.username)}</span>` +
          `<span>${entry.score}</span>` +
          `<span>${entry.kills}</span>`;
        table.appendChild(row);
      });
      overlay.appendChild(table);
    }

    // Action row sits below the leaderboard. Default state: "REMATCH". On
    // RematchFailed the GameClient flips it to "RETURN TO MENU" via
    // `setRematchButtonMenuMode()`.
    const actionRow = document.createElement('div');
    actionRow.className = 'match-end-actions';

    const rematchBtn = document.createElement('button');
    rematchBtn.className = 'match-end-btn rematch-btn';
    rematchBtn.textContent = 'REMATCH';
    rematchBtn.addEventListener('click', () => {
      // The button doubles as the "RETURN TO MENU" trigger after a failed
      // vote — `menuClickHandler` is set by GameClient on RematchFailed.
      if (this.menuClickHandler) {
        this.menuClickHandler();
        return;
      }
      this.rematchClickHandler?.();
    });
    actionRow.appendChild(rematchBtn);
    overlay.appendChild(actionRow);
    this.rematchButton = rematchBtn;

    this.root.appendChild(overlay);
    this.matchEndOverlay = overlay;
  }

  hideMatchEndOverlay(): void {
    if (this.matchEndOverlay) {
      this.matchEndOverlay.remove();
      this.matchEndOverlay = null;
    }
    this.rematchButton = null;
    this.rematchClickHandler = null;
    this.menuClickHandler = null;
    this.rematchModal.hide();
  }

  /** GameClient registers this once per match-end overlay. */
  onRematchClick(handler: () => void): void {
    this.rematchClickHandler = handler;
  }

  /** Visible only after RematchFailed — replaces the rematch action. */
  setRematchButtonMenuMode(handler: () => void): void {
    this.menuClickHandler = handler;
    if (this.rematchButton) {
      this.rematchButton.textContent = 'RETURN TO MENU';
      this.rematchButton.classList.add('menu-mode');
    }
  }

  /** Show the live "Voting for rematch..." modal alongside the overlay. */
  showRematchModal(opts: { votes?: number; required?: number; remainingMs?: number } = {}): void {
    this.rematchModal.show(opts);
  }

  setRematchModalFinalMessage(message: string): void {
    this.rematchModal.showFinalMessage(message);
  }

  /** Disable the rematch button (e.g. while waiting for the new room id). */
  setRematchButtonDisabled(disabled: boolean): void {
    if (this.rematchButton) this.rematchButton.disabled = disabled;
  }

  getLeaderboard(): Leaderboard {
    return this.leaderboard;
  }

  getCardBar(): AlgorithmCardBar {
    return this.cardBar;
  }

  getKillStreakToast(): KillStreakToast {
    return this.killStreakToast;
  }

  getDroneStatus(): DroneStatusPanel {
    return this.droneStatus;
  }

  getMinimap(): MinimapRenderer {
    return this.minimap;
  }

  getBrStatusPanel(): BrStatusPanel {
    return this.brStatus;
  }

  /**
   * Lazily instantiate the diagnostic combat-log panel. GameClient owns the
   * dependency object (selected drone id + clock + name lookup) so HudManager
   * stays decoupled from the network/state layer.
   */
  initCombatLog(deps: CombatLogDeps): CombatLog {
    if (!this.combatLog) {
      this.combatLog = new CombatLog(this.hud, deps);
    }
    return this.combatLog;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
